import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Carousel } from '../components/Carousel';
import { Button } from 'ui-components/build';
import { Input } from 'ui-components/build';
import { useGiftees } from '../context/GifteesProvider';
import { useShoppingCart } from '../context/ShoppingCartProvider';
import { FullscreenSpinner } from '../components/FullscreenSpinner';
import { Giftee } from '../types/Giftee';
import { useQuery, useQueryCache } from 'react-query';
import { WebsiteProduct } from 'shared/build/types';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { customFetch } from '../util/functions';
import { Spinner } from '../components/Spinner';
import { Money } from 'shared/build/Money';
import { useAuthentication } from '../context/AuthenticationProvider';
import { toast } from 'react-toastify';
import { useParents } from '../context/ParentsProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const ScWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ScProductTitle = styled.h4`
  margin: 10px 0;
`;

const ScProductPrice = styled.h5`
  margin: 10px 0;
`;

const ScQuantityAndBuy = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 10px;

  button.addOrSubtract {
    width: 50px;
    margin: 0 5px;
    font-weight: bold;
  }

  input {
    width: 50px;
  }
`;

const ScDetailsSection = styled.section`
  margin: 20px 0;
  h6 {
    margin: 0;
  }
`;

const ScNote = styled.p`
  font-size: 0.65em;
`;

const ScNavBack = styled.p`
  margin: 5px 0;

  &:hover {
    cursor: pointer;
  }
`;

export const Product = () => {
  const history = useHistory();
  const location = useLocation<{ origin?: string }>();
  const { giftees } = useGiftees();
  const { addItem } = useShoppingCart();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthentication();
  const { parents } = useParents();
  const queryCache = useQueryCache();

  const [quantityToGive, setQuantityToGive] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const queryKey = ['website-product-get', id || ''];

  const { data: websiteProduct, status } = useQuery<
    WebsiteProduct,
    [string, string]
  >(
    queryKey,
    async (): Promise<WebsiteProduct> => {
      const response = await customFetch(`/api/website/product/${id}`);
      if (response.ok) {
        return response.json();
      }

      toast.error('Ocorreu um erro ao carregar o produto.');

      setIsLoading(false);
      throw new Error(response.toString());
    }
  );

  useEffect(() => {
    if (!websiteProduct) return;

    setQuantityToGive(websiteProduct.cart.quantity || 1);
  }, [websiteProduct]);

  if (status === 'loading' || !websiteProduct || !user) {
    return (
      <ScWrapper>
        <Spinner />
      </ScWrapper>
    );
  }

  const canNavigateBack = location?.state?.origin;

  return (
    <ScWrapper className="babys-page-padding">
      <FullscreenSpinner show={isLoading} />
      {canNavigateBack && (
        <ScNavBack role="link" onClick={() => history.goBack()}>
          <FontAwesomeIcon icon={faArrowLeft} /> {location.state.origin}
        </ScNavBack>
      )}
      <ScProductTitle>
        {websiteProduct.product.title}
        {websiteProduct.product.alreadyGiven && '*'}
      </ScProductTitle>
      <ScProductPrice>
        {new Money(
          websiteProduct.product.price,
          websiteProduct.product.currency
        ).toString()}
      </ScProductPrice>
      <Carousel
        imagesIds={
          websiteProduct.product.imagesIds.map((img) => ({
            alt: websiteProduct.product.title,
            id: img,
          })) || []
        }
      />
      <ScQuantityAndBuy>
        <Button
          className="addOrSubtract"
          onClick={() => {
            if (quantityToGive === 0) return;

            setQuantityToGive((quantityToGive || 0) - 1);
          }}
        >
          -
        </Button>
        <Input
          type="number"
          value={quantityToGive}
          onChange={(e) => setQuantityToGive(e.target.valueAsNumber)}
        />
        <Button
          className="addOrSubtract"
          onClick={() => setQuantityToGive((quantityToGive || 0) + 1)}
        >
          +
        </Button>
        <Button
          onClick={async () => {
            setIsLoading(true);
            const hasAddedItem = await addItem({
              productId: websiteProduct.product.id,
              quantity: quantityToGive,
            });
            setIsLoading(false);

            if (!hasAddedItem) return;

            queryCache.setQueryData(queryKey, {
              ...websiteProduct,
              cart: {
                ...websiteProduct.cart,
                quantity: quantityToGive,
              },
            });

            toast.success(
              `Você adicionou o presente "${websiteProduct.product.title}" ao Cesto de Presentes.`
            );

            if (canNavigateBack) {
              history.goBack();
            } else {
              history.push('/inicio');
            }
          }}
        >{`Presentear ${Giftee.joinGiftees(giftees, 'e')}`}</Button>
        {/* TODO: Add "thermomether" to allow users to see how much was given... */}
        {/* TODO: Allow fractioning the amount given. */}
      </ScQuantityAndBuy>
      {websiteProduct.product.details && (
        <ScDetailsSection>
          <h6>Detalhes</h6>
          <p>{websiteProduct.product.details}</p>
        </ScDetailsSection>
      )}
      <ScDetailsSection>
        {websiteProduct.product.alreadyGiven && (
          <ScNote>
            * Este presente já foi ofertado. Mesmo assim outra oferta é bem
            vinda e será convertida em outros produtos ou serviços para o bebê.
          </ScNote>
        )}
        <ScNote>
          As imagens dos produtos são ilustrativas. No entanto,{' '}
          {parents.join(' e ')} se{' '}
          {parents.length === 1 ? 'compromete' : 'comprometem'} a tentar comprar
          presentes semelhantes àqueles apresentados neste site.
        </ScNote>
      </ScDetailsSection>
    </ScWrapper>
  );
};
